import { AnalyticsProps, TrackKind } from '../api/analyticsApi/analyticsApi';
import { pageOf, trackClick } from './tracker';

/**
 * Clicks on elements tagged for tracking, recorded as ui.clicked:
 *
 *   <Button {...trackAttributes(TRACK.cases.export, 'button')}>Export</Button>
 *   <Box {...trackAttributes(TRACK.dashboard.deductions.needsAttention, 'tile', { count })} />
 *
 * One listener on the document finds the closest tagged element of every click. It runs in the
 * capture phase, before the element's own handler, so the details describe what the user saw
 * when clicking. Untagged elements are never recorded, and an element's text is never read.
 */

export interface TrackAttributes {
  'data-track': string;
  'data-track-kind'?: TrackKind;
  'data-track-props'?: string;
}

/** Clicks on the same element within this time count once (a double click is one click). */
const REPEAT_MS = 500;

/**
 * The attributes that make an element tracked: its id (from TRACK in catalog.ts), what kind of
 * element it is (worked out from the element when left out) and small details.
 */
export const trackAttributes = (
  target: string,
  kind?: TrackKind,
  props?: AnalyticsProps,
): TrackAttributes => ({
  'data-track': target,
  ...(kind ? { 'data-track-kind': kind } : {}),
  ...(props && Object.keys(props).length > 0 ? { 'data-track-props': JSON.stringify(props) } : {}),
});

const kindOf = (element: Element): TrackKind => {
  const role = element.getAttribute('role');
  if (element.tagName === 'A' || role === 'link') return 'link';
  if (role === 'tab') return 'tab';
  if (role === 'menuitem') return 'menu';
  if (role === 'switch' || role === 'checkbox' || role === 'radio') return 'toggle';
  if (element.tagName === 'BUTTON' || role === 'button') return 'button';
  return 'other';
};

const propsOf = (element: Element): AnalyticsProps => {
  const raw = element.getAttribute('data-track-props');
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as AnalyticsProps)
      : {};
  } catch {
    return {};
  }
};

/** A link's destination: the route template inside the app, never the URL itself. */
const destinationOf = (element: Element): AnalyticsProps => {
  const href = element.closest('a[href]')?.getAttribute('href');
  if (!href) return {};
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return { external: true };
    const to = pageOf(url.pathname)?.page;
    return to ? { to } : {};
  } catch {
    return {};
  }
};

let lastClick: { target: string; at: number } | undefined;

const onClick = (event: MouseEvent): void => {
  if (!(event.target instanceof Element)) return;
  const element = event.target.closest('[data-track]');
  if (!element) return;

  const target = element.getAttribute('data-track') ?? '';
  const now = Date.now();
  if (lastClick && lastClick.target === target && now - lastClick.at < REPEAT_MS) return;
  lastClick = { target, at: now };

  const kind = (element.getAttribute('data-track-kind') as TrackKind | null) ?? kindOf(element);
  trackClick({ target, kind, props: { ...propsOf(element), ...destinationOf(element) } });
};

/** Starts recording clicks on tracked elements; returns a cleanup function. */
export const startClickTracking = (): (() => void) => {
  document.addEventListener('click', onClick, true);
  return () => document.removeEventListener('click', onClick, true);
};
